import React, { useLayoutEffect, useRef, useEffect, useState } from "react";
import Footer from "./components/Footer";

const SLIDER_PHOTOS = [
  "/images/fotos_grid/OpenSpace2025-0132.jpg",
  "/images/fotos_grid/OpenSpace2025-3916.jpg",
  "/images/fotos_grid/OpenSpace2025-3339.jpg",
  "/images/fotos_grid/OpenSpace2025-2945.jpg",
  "/images/fotos_grid/OpenSpace2025-0229.jpg",
  "/images/fotos_grid/OpenSpace2025-3716.jpg",
  "/images/fotos_grid/OpenSpace2025-3154.jpg",
  "/images/fotos_grid/OpenSpace2025-0081.jpg",
  "/images/fotos_grid/OpenSpace2025-2803.jpg",
  "/images/fotos_grid/OpenSpace2025-0028 (1).jpg",
  "/images/fotos_grid/OpenSpace2025-0259.jpg",
  "/images/fotos_grid/OpenSpace2025-3179.jpg",
  "/images/fotos_grid/OpenSpace2025-0336.jpg",
  "/images/fotos_grid/OpenSpace2025-3931.jpg",
  "/images/fotos_grid/OpenSpace2025-0408.jpg",
  "/images/fotos_grid/OpenSpace2025-2936.jpg",
  "/images/fotos_grid/OpenSpace2025-0003.jpg",
  "/images/fotos_grid/OpenSpace2025-3247.jpg",
];

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

const FOCUS_RING_ON_MEDIA = `${FOCUS_RING} focus-visible:ring-4 focus-visible:ring-[#040066]`;

const CTA_CLASS = `min-h-12 md:min-h-14 px-4 md:px-6 py-2 rounded inline-flex justify-center items-center text-Colors-white text-xl font-bold leading-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/25 motion-reduce:transition-none motion-reduce:hover:scale-100 ${FOCUS_RING}`;

const WaitlistCtaText = () => (
  <>
    Plazas agotadas
    <span className="sr-only">. Ir a la lista de espera</span>
  </>
);

const EventIcon: React.FC<{ src: string }> = ({ src }) => (
  <span
    className="w-[47px] h-[47px] md:w-[59px] md:h-[59px] box-border p-[3.5px] rounded-full outline outline-1 outline-white/40 flex items-center justify-center overflow-hidden flex-shrink-0"
    aria-hidden="true"
  >
    <img className="w-full h-full" src={src} alt="" />
  </span>
);

const Home: React.FC = () => {
  const [sliderIndex, setSliderIndex] = useState(0);
  const [sliderPaused, setSliderPaused] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lumaIframeRef = useRef<HTMLIFrameElement>(null);
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const handRef = useRef<HTMLImageElement>(null);
  const heroRowRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);

  const FINGER_X_FRACTION = 0.07;
  const FINGER_Y_FRACTION = 0.11;
  const MAX_HAND_WIDTH = 820;
  const BASE_PADDING_BOTTOM = 24;
  const BOTTOM_BUFFER = 0;

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      console.log("[iframe message]", e.origin, e.data);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      if (mq.matches) setSliderPaused(true);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (videoPlaying) videoIframeRef.current?.focus();
  }, [videoPlaying]);

  useEffect(() => {
    if (sliderPaused) return;
    autoplayRef.current = setInterval(() => {
      setSliderIndex((prev) => (prev + 1) % SLIDER_PHOTOS.length);
    }, 4000);
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [sliderPaused]);

  const handleDateRect = () => {
    requestAnimationFrame(() => {
      if (
        dateRef.current &&
        handRef.current &&
        heroRowRef.current &&
        heroSectionRef.current
      ) {
        const dateRect = dateRef.current.getBoundingClientRect();
        const containerRect = heroRowRef.current.getBoundingClientRect();
        const naturalWidth = handRef.current.naturalWidth || 1690;
        const naturalHeight = handRef.current.naturalHeight || 1976;
        const aspectRatio = naturalHeight / naturalWidth;

        const viewportRight = window.innerWidth;
        const rawWidth =
          ((viewportRight - dateRect.right) / (1 - FINGER_X_FRACTION)) * 1.25;
        const width = Math.max(0, Math.min(MAX_HAND_WIDTH, rawWidth));
        const height = width * aspectRatio;

        const top =
          dateRect.top - containerRect.top - height * FINGER_Y_FRACTION;
        const left =
          dateRect.right - containerRect.left - width * FINGER_X_FRACTION;

        handRef.current.style.top = `${top}px`;
        handRef.current.style.left = `${left}px`;
        handRef.current.style.width = `${width}px`;
        handRef.current.style.height = `${height}px`;

        const handBottom = dateRect.top + height * (1 - FINGER_Y_FRACTION);
        const contentBottom = containerRect.bottom;
        const extra = Math.max(0, handBottom - contentBottom + BOTTOM_BUFFER);
        heroSectionRef.current.style.paddingBottom =
          window.innerWidth > 2250
            ? "6rem"
            : `${BASE_PADDING_BOTTOM + extra}px`;
      }
    });
  };

  useLayoutEffect(() => {
    handleDateRect();

    const resizeObserver = new ResizeObserver(() => {
      handleDateRect();
    });

    resizeObserver.observe(dateRef.current!);

    window.addEventListener("resize", handleDateRect);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleDateRect);
    };
  }, []);

  const goToSlide = (index: number) => {
    setSliderPaused(true);
    setSliderIndex(index);
  };

  return (
    <div className="w-full min-w-0 flex flex-col justify-start items-center min-h-screen overflow-x-hidden pt-28 md:pt-40 lg:pt-60">
      <section
        ref={heroSectionRef}
        aria-labelledby="home-title"
        className="w-full pb-6 border-b border-white/40 flex flex-col justify-start items-center [border-style:dashed]"
      >
        <div className="w-full max-w-[1400px] 3xl:max-w-[1920px] px-8 lg:pr-[220px] xl:pr-[260px] 3xl:pl-16 3xl:pr-[300px]">
          <div
            ref={heroRowRef}
            className="relative w-full flex flex-col lg:flex-row justify-start items-center gap-8 lg:gap-12 xl:gap-16 3xl:gap-24"
          >
            <div className="w-full lg:flex-1">
              <p className="text-Colors-white text-lg md:text-xl font-bold mb-4 md:mb-6">
                Un año más en Pamplona…
              </p>
              <h1
                id="home-title"
                className="w-full text-Colors-white font-semibold uppercase mb-4 md:mb-6 text-7xl sm:text-4xl md:text-6xl lg:text-8xl leading-[1.15]"
              >
                Open Space 2026
              </h1>
              <p className="text-Colors-white text-lg md:text-2xl font-normal leading-6 md:leading-7 mb-2 md:mb-4">
                Ven a construir el contenido, no solo a vivirlo.
              </p>
             {/*  <p className="text-Colors-white text-base md:text-lg font-normal leading-6 md:leading-7 mb-8 md:mb-12">
                Propuesta de charlas abierta hasta el 2 de septiembre (incluido).
              </p> */}
              <div className="w-full flex justify-start items-start mb-6 md:mb-8">
                <a
                  href="#inscripcion-luma"
                  className={`${CTA_CLASS} mr-4 md:mr-6`}
                  style={{ background: "var(--Colors-red, #F00A0A)" }}
                >
                  <WaitlistCtaText />
                </a>
                {/* <a
                  href="https://charlas-open-space.jakala.es/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={CTA_CLASS}
                  style={{ background: "rgba(255, 255, 255, 0.2)" }}
                >
                  Propón tu charla
                  <span className="sr-only"> (se abre en una pestaña nueva)</span>
                </a> */}
              </div>
            </div>

            <div
              ref={dateRef}
              className="w-full lg:flex-1 lg:max-w-96 p-4 md:p-6 rounded-2xl outline outline-1 outline-offset-[-1px] outline-white/40 [outline-style:dashed] flex flex-col justify-start items-start gap-3 relative"
            >
              <h2 id="event-data-heading" className="sr-only">
                Datos del evento
              </h2>
              <dl className="w-full flex flex-col gap-3">
                <div className="w-full inline-flex justify-start items-start gap-3">
                  <EventIcon src="/images/calendario.svg" />
                  <div className="flex-1 flex flex-col justify-start items-start gap-1">
                    <dt className="text-Colors-white text-lg md:text-xl font-bold leading-snug">
                      Apunta la fecha
                    </dt>
                    <dd className="text-Colors-white text-base md:text-xl font-normal leading-normal">
                      <time dateTime="2026-09-18">
                        18 de septiembre de 2026.
                        <br /> Viernes
                      </time>
                    </dd>
                  </div>
                </div>
                <div
                  className="w-full h-px border-t border-white/40 [border-style:dashed]"
                  aria-hidden="true"
                />
                <div className="w-full inline-flex justify-start items-start gap-3">
                  <EventIcon src="/images/reloj.svg" />
                  <div className="flex-1 flex flex-col justify-start items-start gap-1">
                    <dt className="text-Colors-white text-lg md:text-xl font-bold leading-snug">
                      Horario
                    </dt>
                    <dd className="text-Colors-white text-base md:text-xl font-normal leading-normal">
                      <time dateTime="2026-09-18T09:00">09:00</time>
                      {" - "}
                      <time dateTime="2026-09-18T17:00">17:00</time> h
                    </dd>
                  </div>
                </div>
                <div
                  className="w-full h-px border-t border-white/40 [border-style:dashed]"
                  aria-hidden="true"
                />
                <div className="w-full inline-flex justify-start items-start gap-3">
                  <EventIcon src="/images/lugar.svg" />
                  <div className="flex-1 flex flex-col justify-start items-start gap-1">
                    <dt className="text-Colors-white text-lg md:text-xl font-bold leading-snug">
                      Lugar
                    </dt>
                    <dd className="text-Colors-white text-base md:text-xl font-normal leading-normal">
                      <address className="not-italic">
                        CEIN, Noain.{" "}
                        <a
                          href="https://maps.app.goo.gl/u1Bjrz11JTEkqmVK7"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline hover:text-jakala-red transition-colors duration-200 ${FOCUS_RING}`}
                        >
                          Localizar
                          <span className="sr-only">
                            {" "}
                            (se abre en Google Maps, pestaña nueva)
                          </span>
                        </a>
                      </address>
                    </dd>
                  </div>
                </div>
                <div
                  className="w-full h-px border-t border-white/40 [border-style:dashed]"
                  aria-hidden="true"
                />
                <div className="w-full inline-flex justify-start items-start gap-3">
                  <EventIcon src="/images/cohete.svg" />
                  <div className="flex-1 flex flex-col justify-start items-start gap-1">
                    <dt className="text-Colors-white text-lg md:text-xl font-bold leading-snug">
                      Precio
                    </dt>
                    <dd className="text-Colors-white text-base md:text-xl font-normal leading-normal">
                      Gratuito. <br /> ¡Pero con plazas limitadas!
                    </dd>
                  </div>
                </div>
              </dl>
              <span
                className="gomet hidden sm:block w-7 h-7 lg:w-11 lg:h-11 absolute top-[70px] md:top-[70px] left-[-10px] md:left-[-20px]"
                aria-hidden="true"
              />
              <span
                className="gomet hidden sm:block w-7 h-7 lg:w-11 lg:h-11 absolute top-[120px] md:top-[120px] right-[-10px] md:right-[-20px]"
                aria-hidden="true"
              />
              <span
                className="gomet hidden sm:block w-7 h-7 lg:w-11 lg:h-11 absolute top-[initial] bottom-[25px] md:bottom-[0px] left-[initial] right-[-37px] md:right-[-20px] z-[2]"
                aria-hidden="true"
              />
              <span
                className="gomet hidden sm:block w-7 h-7 lg:w-11 lg:h-11 left-[initial] top-[initial] right-[-25px] md:right-[-45px] bottom-[-10px] md:bottom-[-20px] absolute"
                aria-hidden="true"
              />
              <span
                className="gomet hidden sm:block w-7 h-7 lg:w-11 lg:h-11 left-[initial] right-[70px] top-[initial] bottom-[-20px] absolute"
                aria-hidden="true"
              />
              <img
                src="/images/mano2.png"
                alt=""
                aria-hidden="true"
                className="lg:hidden absolute top-[60px] max-[400px]:top-[90px] sm:-top-[2px] -right-28 max-[400px]:-right-32 sm:-right-28 w-64 max-[400px]:w-72 sm:w-72 object-contain pointer-events-none opacity-95 z-[1]"
              />
            </div>
            <img
              ref={handRef}
              src="/images/mano2.png"
              alt=""
              aria-hidden="true"
              onLoad={handleDateRect}
              className="hidden lg:block min-[2251px]:hidden absolute object-contain pointer-events-none"
            />
          </div>
        </div>
      </section>

      <section
        aria-labelledby="about-heading"
        className="w-full pt-8 flex flex-col justify-start items-center relative mb-8"
      >
        <div className="w-full max-w-[1400px] 3xl:max-w-[1920px] px-8 3xl:pl-16">
          <h2
            id="about-heading"
            className="w-full text-Colors-white text-2xl md:text-4xl font-normal leading-7 md:leading-9 mb-4"
          >
            <span className="font-bold">Nuestro Open, se vive.</span>
          </h2>
          <p className="mb-4 text-Colors-white text-base md:text-xl font-normal leading-relaxed md:leading-loose">
            Tiene sus propias reglas. Su propia forma de funcionar. Y 22
            ediciones respaldando una idea muy simple:{" "}
            <strong className="font-bold">
              cuando juntas a las personas adecuadas y eliminas el guion, las
              conversaciones cambian de nivel.
            </strong>
          </p>
          <p className="mb-4 text-Colors-white text-base md:text-xl font-normal leading-relaxed md:leading-loose">
            Aquí no vienes a escuchar durante horas. Vienes a proponer, debatir,
            cuestionar, aprender e inspirar. Sin jerarquías. Sin performance.
            Solo talento compartido.
          </p>
          <p className="mb-4 text-Colors-white text-base md:text-xl font-normal leading-relaxed md:leading-loose">
            <strong className="font-bold">
              Más de 150 asistentes y más de 44 empresas después,
            </strong>{" "}
            una cosa sigue siendo la misma: cada edición deja ideas y
            sensaciones que se impregnan entre todos los asistentes durante
            meses.
          </p>
        </div>
      </section>

      <section
        aria-labelledby="how-heading"
        className="w-full flex flex-col justify-start items-center relative mb-8"
      >
        <div className="w-full max-w-[1400px] 3xl:max-w-[1920px] px-8 3xl:pl-16">
          <h2
            id="how-heading"
            className="w-full text-Colors-white text-2xl md:text-4xl font-bold leading-7 md:leading-9 mt-6 mb-4 md:mb-6"
          >
            ¿En qué consiste nuestro Open Space?
          </h2>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed md:leading-loose mb-4">
            <strong className="font-bold">Ni todo improvisado. Ni todo cerrado. </strong>
          </p>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed mb-4">
            Un Open de verdad deja espacio para que pasen cosas. El nuestro,
            además, se asegura de que las mejores no se queden fuera.
          </p>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed mb-4">
            Así es como construimos la agenda:
          </p>
          <ul
            role="list"
            className="w-full flex flex-col justify-start items-start gap-3 list-disc list-outside marker:text-Colors-light-blue pl-6 mb-4"
          >
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              <strong className="font-bold">Antes del evento: </strong>
              proponéis las charlas. (Cerrado)
            </li>
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              <strong className="font-bold">
                Periodo de votación:</strong> Entre las propuestas, puedes votar las que
                más te gusten. (Cerrado)
            </li>
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              <strong className="font-bold">Resultado: </strong>
              las más interesantes se ganan un hueco en la agenda. (Agenda cerrada)
            </li>
          </ul>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed mb-4">
            Pero el Open no termina ahí. Cuando llega el día, la conversación
            vuelve a empezar.
          </p>
          <ul
            role="list"
            className="w-full flex flex-col justify-start items-start gap-3 list-disc list-outside marker:text-Colors-light-blue pl-6 mb-4"
          >
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              Nuevas propuestas.
            </li>
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              Nuevas votaciones.
            </li>
            <li className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed">
              Nuevas sesiones que nacen en tiempo real.
            </li>
          </ul>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed mb-4">
            Porque las mejores ideas pueden surgir semanas antes... o cinco
            minutos antes de empezar.
          </p>
          <p className="w-full text-Colors-white text-base md:text-xl font-normal leading-relaxed md:leading-loose mb-6">
            Y en este Open, las dos tienen sitio.
          </p>
        </div>
      </section>

      <section
        aria-labelledby="video-heading"
        className="w-full relative flex justify-center items-center px-4 md:px-8 pt-8 pb-32"
      >
        <h2 id="video-heading" className="sr-only">
          Vídeo de la edición 2025
        </h2>
        <div className="w-full max-w-[1500px]" style={{ aspectRatio: "16 / 9" }}>
          {!videoPlaying ? (
            <button
              type="button"
              onClick={() => setVideoPlaying(true)}
              className={`relative w-full h-full cursor-pointer rounded-lg overflow-hidden ${FOCUS_RING_ON_MEDIA}`}
              aria-label="Reproducir vídeo: Open Space JAKALA 2025"
            >
              <img
                src="/images/fotos_grid/OpenSpace2025-0336.jpg"
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 md:w-10 md:h-10 text-Colors-light-blue ml-1"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            </button>
          ) : (
            <iframe
              ref={videoIframeRef}
              className="w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/g-OFdnbeheM?si=kT-1VOVC-lnzAztY&autoplay=1&cc_load_policy=1"
              title="Open Space JAKALA 2025 | Innovación colaborativa en Pamplona"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          )}
        </div>
      </section>

      <section
        aria-labelledby="reasons-heading"
        className="w-full py-8 md:py-16 relative bg-Colors-white flex flex-col justify-start items-center"
      >
        <div className="w-full max-w-[1400px] 3xl:max-w-[1920px] px-8 3xl:pl-16">
          <h2
            id="reasons-heading"
            className="w-full text-Colors-light-blue text-2xl md:text-4xl font-bold leading-7 md:leading-9 mb-6 md:mb-8"
          >
            3 razones para no perdértelo
          </h2>
          <ol
            role="list"
            className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12 list-none"
          >
            <li className="flex flex-col justify-start items-start gap-3">
              <h3 className="text-Colors-light-blue text-xl md:text-2xl font-bold leading-relaxed">
                No eres un espectador.
              </h3>
              <p className="text-Colors-gray-dark text-base md:text-lg font-normal leading-relaxed md:leading-loose">
                Aquí no vienes solo a consumir contenido. También vienes a
                construirlo. Propón, vota y forma parte de un evento que cambia
                en cada edición.
              </p>
            </li>
            <li className="flex flex-col justify-start items-start gap-3">
              <h3 className="text-Colors-light-blue text-xl md:text-2xl font-bold leading-relaxed">
                Sal con más preguntas.
              </h3>
              <p className="text-Colors-gray-dark text-base md:text-lg font-normal leading-relaxed md:leading-loose">
                Y mejores respuestas porque las conversaciones más interesantes
                no siempre confirman lo que ya sabes. IA, tecnología, datos,
                marketing, innovación... Charlas nacidas de todos y todas, para
                todos y todas. Lo que interesa de verdad.
              </p>
            </li>
            <li className="flex flex-col justify-start items-start gap-3 md:col-span-2 lg:col-span-1">
              <h3 className="text-Colors-light-blue text-xl md:text-2xl font-bold leading-relaxed">
                Encuentra a tu gente.
              </h3>
              <p className="text-Colors-gray-dark text-base md:text-lg font-normal leading-relaxed md:leading-loose">
                Más de 150 profesionales con las mismas ganas de compartir
                ideas, debatir y descubrir nuevas perspectivas. Vuelve a casa
                con conversaciones que seguirán mucho después del evento.
              </p>
            </li>
          </ol>
        </div>
      </section>

      <section
        aria-labelledby="cta-heading"
        className="w-full py-8 md:py-16 px-8 3xl:pl-16 bg-Colors-blue-light flex flex-wrap items-center justify-center gap-8 md:gap-16"
      >
        <h2
          id="cta-heading"
          className="text-Colors-white text-4xl md:text-5xl font-normal leading-tight"
        >
          ¿Quieres venir al <br className="max-[425px]:block hidden" /> Open
          Space 2026?
        </h2>
        <a
          href="#inscripcion-luma"
          className={`w-full max-w-[21.875rem] ${CTA_CLASS}`}
          style={{ background: "var(--Colors-red, #F00A0A)" }}
        >
          <WaitlistCtaText />
        </a>
      </section>

      <section
        aria-labelledby="photos-heading"
        aria-roledescription="carrusel"
        className="w-full py-12 md:py-20 overflow-hidden"
      >
        <h2 id="photos-heading" className="sr-only">
          Fotos de ediciones anteriores
        </h2>
        <div className="w-full max-w-[900px] 3xl:max-w-[1100px] mx-auto px-4 md:px-8 relative">
          <div
            ref={sliderRef}
            id="photo-slider"
            className="relative w-full overflow-hidden rounded-xl"
            style={{ aspectRatio: "16 / 9" }}
          >
            {SLIDER_PHOTOS.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={
                  i === sliderIndex
                    ? `Foto ${i + 1} de ${SLIDER_PHOTOS.length} de ediciones anteriores del Open Space`
                    : ""
                }
                aria-hidden={i !== sliderIndex}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out motion-reduce:transition-none"
                style={{ opacity: i === sliderIndex ? 1 : 0 }}
              />
            ))}
          </div>
          <p className="sr-only" aria-live={sliderPaused ? "polite" : "off"} aria-atomic="true">
            Foto {sliderIndex + 1} de {SLIDER_PHOTOS.length}
          </p>

          <button
            type="button"
            onClick={() =>
              goToSlide(
                (sliderIndex - 1 + SLIDER_PHOTOS.length) % SLIDER_PHOTOS.length,
              )
            }
            className={`absolute left-6 md:left-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-Colors-blue-dark hover:bg-black flex items-center justify-center text-white transition-colors ${FOCUS_RING_ON_MEDIA}`}
            aria-controls="photo-slider"
            aria-label="Foto anterior"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => goToSlide((sliderIndex + 1) % SLIDER_PHOTOS.length)}
            className={`absolute right-6 md:right-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-Colors-blue-dark hover:bg-black flex items-center justify-center text-white transition-colors ${FOCUS_RING_ON_MEDIA}`}
            aria-controls="photo-slider"
            aria-label="Foto siguiente"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div className="flex justify-center items-center gap-1 mt-4">
            {SLIDER_PHOTOS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToSlide(i)}
                className={`flex items-center justify-center w-6 h-6 ${FOCUS_RING} rounded-full`}
                aria-label={`Ir a foto ${i + 1} de ${SLIDER_PHOTOS.length}`}
                aria-current={i === sliderIndex ? "true" : undefined}
                aria-controls="photo-slider"
              >
                <span
                  className={`rounded-full transition-all duration-300 ${
                    i === sliderIndex
                      ? "bg-white w-6 h-2"
                      : "bg-white/70 hover:bg-white w-2 h-2"
                  }`}
                />
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSliderPaused((prev) => !prev)}
              className={`ml-3 w-8 h-8 flex items-center justify-center rounded-full text-white bg-Colors-blue-dark border border-white/60 hover:bg-black transition-colors ${FOCUS_RING}`}
              aria-controls="photo-slider"
              aria-label={
                sliderPaused
                  ? "Reanudar carrusel de fotos"
                  : "Pausar carrusel de fotos"
              }
            >
              <svg
                className={`w-4 h-4 ${sliderPaused ? "ml-0.5" : ""}`}
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                {sliderPaused ? (
                  <path d="M8 5v14l11-7z" />
                ) : (
                  <path d="M7 4h4v16H7zm6 0h4v16h-4z" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </section>

      <hr className="w-full h-px border-t border-white/40 [border-style:dashed]" />

      <section
        aria-labelledby="faq-heading"
        className="w-full py-8 md:py-16 flex flex-col justify-start items-center"
      >
        <div className="w-full max-w-[1400px] 3xl:max-w-[1920px] px-8 3xl:pl-16">
          <h2
            id="faq-heading"
            className="w-full text-center text-Colors-white text-xl md:text-2xl font-bold leading-relaxed mb-3 md:mb-4"
          >
            Preguntas frecuentes
          </h2>
          <dl className="w-full grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
            <div className="flex flex-col items-center gap-2">
              <dt className="w-full text-center text-Colors-white text-sm font-bold leading-normal">
                ¿Puedo proponer una charla?
              </dt>
              <dd className="w-full text-center text-Colors-white text-base font-normal leading-normal">
               Sí, el mismo día del evento habrá un espacio para proponer tu charla sobre el tema que prefieras.
              </dd>
            </div>
            <div className="flex flex-col items-center gap-2">
              <dt className="w-full text-center text-Colors-white text-sm font-bold leading-normal">
                Si propongo una charla, ¿presento 100%?
              </dt>
              <dd className="w-full text-center text-Colors-white text-base font-normal leading-normal">
                No, puedes proponer una charla, pero puede no ser votada por la
                mayoría.
              </dd>
            </div>
            <div className="flex flex-col items-center gap-2">
              <dt className="w-full text-center text-Colors-white text-sm font-bold leading-normal">
                ¿Tengo que ser profesional en algún sector o área?
              </dt>
              <dd className="w-full text-center text-Colors-white text-base font-normal leading-normal">
                Para nada, esto va desde novatos hasta expertos.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        id="inscripcion-luma"
        aria-labelledby="signup-heading"
        tabIndex={-1}
        className="w-full flex flex-col items-center scroll-mt-28 md:scroll-mt-40 lg:scroll-mt-60 outline-none"
      >
        <div className="w-full pt-8 bg-Colors-blue-light">
          <h2
            id="signup-heading"
            className="text-Colors-white text-2xl md:text-4xl font-bold text-center mb-8 px-4"
          >
            ¿Te has quedado sin plaza? ¡Apúntate a la lista de espera!
          </h2>
        </div>
        <div className="w-full" style={{ backgroundColor: "#f0f0ff" }}>
          <iframe
            ref={lumaIframeRef}
            src="https://lu.ma/embed/event/OpenSpacebyJAKALA/simple"
            title="Formulario de inscripción al Open Space 2026"
            width="100%"
            height="500"
            allowFullScreen
          />
          <p className="w-full text-center text-base pb-4">
            <a
              href="https://lu.ma/OpenSpacebyJAKALA"
              target="_blank"
              rel="noopener noreferrer"
              className="text-Colors-blue-dark underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-Colors-blue-dark"
            >
              Abrir el formulario de inscripción en Luma
              <span className="sr-only"> (se abre en una pestaña nueva)</span>
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
